import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { Mutex } from 'async-mutex';
import { RootState } from '../app/store';
import { setCredentials, logOut } from '../features/auth/authSlice';
import { globalRateLimiter } from '../utils/rateLimiter';

// Create a new mutex
const mutex = new Mutex();

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true, // Important for HttpOnly cookies
});

interface BaseQueryError {
    status?: number;
    data?: any;
}

// Explicitly typed axiosBaseQuery
export const axiosBaseQuery =
    (
        { baseUrl }: { baseUrl: string } = { baseUrl: '' }
    ): BaseQueryFn<
        {
            url: string;
            method?: AxiosRequestConfig['method'];
            data?: AxiosRequestConfig['data'];
            body?: any; // Alias for data
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
            responseType?: AxiosRequestConfig['responseType'];
        },
        unknown,
        unknown
    > =>
        async (args, api, extraOptions) => {
            try {
                const { url, method, data, body, params, headers, responseType } = typeof args === 'string'
                    ? { url: args, method: undefined, data: undefined, body: undefined, params: undefined, headers: undefined, responseType: undefined }
                    : args;

                const requestData = data || body; // Support both properties

                const token = (api.getState() as RootState).auth.accessToken;
                if ((baseUrl + url).includes('undefined')) {
                    // console.error('BaseQuery: Requesting undefined URL', { url, baseUrl, fullUrl: baseUrl + url, args });
                }

                const result = await axiosInstance({
                    url: baseUrl + url,
                    method,
                    data: requestData,
                    params,
                    responseType: responseType as any,
                    headers: {
                        ...headers,
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                });
                return { data: result.data };
            } catch (axiosError) {
                const err = axiosError as AxiosError;
                return {
                    error: {
                        status: err.response?.status,
                        data: err.response?.data || err.message,
                    },
                };
            }
        };

export const baseQueryWithReauth: BaseQueryFn<
    string | { url: string; method: AxiosRequestConfig['method']; data?: any; params?: any },
    unknown,
    unknown
> = async (args, api, extraOptions) => {
    // 1. Client-side Rate Limiting
    if (!globalRateLimiter.allow()) {
        return {
            error: {
                status: 429,
                data: { message: 'Too many requests. Please slow down.' },
            },
        };
    }

    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();

    let result = await axiosBaseQuery({ baseUrl: '' })(args as any, api, extraOptions);

    if (result.error && (result.error as BaseQueryError).status === 401) {
        const errorData = (result.error as any).data?.error || (result.error as any).data;
        const errorCode = errorData?.code;

        // If the session is stale (role update) or account is disabled, do NOT try to reauth.
        // Logout immediately to apply security changes.
        // if (errorCode === 'STALE_SESSION' || errorCode === 'ACCOUNT_DISABLED') {
        //     api.dispatch(logOut());
        //     return result;
        // }
        // Only logout on disabled accounts. Let STALE_SESSION trigger the silent refresh below!
        if (errorCode === 'ACCOUNT_DISABLED') {
            api.dispatch(logOut());
            return result;
        }

        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const refreshResult = await axiosBaseQuery({ baseUrl: '' })(
                    { url: '/auth/refresh', method: 'POST' },
                    api,
                    extraOptions
                );

                // Backend returns: { success: true, data: { access_token, user } }
                const refreshPayload = (refreshResult.data as any)?.data;
                const newAccessToken = refreshPayload?.access_token;
                const refreshedUser = refreshPayload?.user;

                if (newAccessToken) {
                    api.dispatch(setCredentials({
                        user: refreshedUser,
                        accessToken: newAccessToken,
                    }));

                    // Retry the initial query with the new token
                    result = await axiosBaseQuery({ baseUrl: '' })(args as any, api, extraOptions);
                } else {
                    api.dispatch(logOut());
                }
            } finally {
                release();
            }
        } else {
            // wait until the mutex is available without locking it
            await mutex.waitForUnlock();
            result = await axiosBaseQuery({ baseUrl: '' })(args as any, api, extraOptions);
        }
    }
    return result;
};
